import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import {
  filterRecipientContacts,
  shouldShowContactSuggestions,
} from '../../utils/recipientContactsStore';

const RecipientContactAutocomplete = ({
  field,
  value,
  onChange,
  onSelectContact,
  contacts,
  placeholder,
  isInvalid = false,
  feedback = '',
  inputClassName = '',
  iconClassName = '',
  showIcon = false,
}) => {
  const listId = useId();
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const matches = useMemo(
    () => filterRecipientContacts(contacts, value, field),
    [contacts, value, field]
  );

  const showList = open && matches.length > 0 && shouldShowContactSuggestions(field, value);

  useEffect(() => {
    setActiveIndex(-1);
  }, [value, matches.length]);

  useEffect(() => {
    const onDocMouseDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  const pickContact = (contact) => {
    onSelectContact?.(contact);
    setOpen(false);
    setActiveIndex(-1);
  };

  const onKeyDown = (event) => {
    if (!showList) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((prev) => Math.min(prev + 1, matches.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, 0));
    } else if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      pickContact(matches[activeIndex]);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  const control = (
    <Form.Control
      type={field === 'email' ? 'email' : 'text'}
      placeholder={placeholder}
      value={value}
      autoComplete="off"
      aria-autocomplete="list"
      aria-controls={showList ? listId : undefined}
      aria-expanded={showList}
      className={inputClassName}
      isInvalid={isInvalid}
      onFocus={() => setOpen(true)}
      onChange={(e) => {
        onChange(e.target.value);
        setOpen(true);
      }}
      onKeyDown={onKeyDown}
    />
  );

  return (
    <div ref={rootRef} className="recipient-contact-autocomplete">
      {showIcon ? (
        <div className="input-with-icon">
          <i className={`${iconClassName} input-icon`} aria-hidden />
          {control}
        </div>
      ) : (
        control
      )}

      {isInvalid && feedback ? (
        <Form.Control.Feedback type="invalid">{feedback}</Form.Control.Feedback>
      ) : null}

      {showList ? (
        <ul id={listId} className="recipient-contact-suggestions" role="listbox">
          {matches.map((contact, index) => (
            <li key={`${contact.email}-${contact.id ?? index}`} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={index === activeIndex}
                className={[
                  'recipient-contact-suggestion',
                  index === activeIndex ? 'is-active' : '',
                ].filter(Boolean).join(' ')}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pickContact(contact)}
                onMouseEnter={() => setActiveIndex(index)}
              >
                <span className="recipient-contact-suggestion-name">
                  {contact.name || '—'}
                </span>
                <span className="recipient-contact-suggestion-email">{contact.email}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

export default RecipientContactAutocomplete;
